import React, { useState, useEffect, useRef } from 'react';
import ideaService from '../services/ideaService';
import tagService from '../services/tagService';
import IdeaForm from './IdeaForm';

const CreateIdeaModal = ({ onClose, onSubmit }) => {
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [contentHeight, setContentHeight] = useState('auto');
  const contentRef = useRef(null);
  const tagInputRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const tagSearchTimerRef = useRef(null);


  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.height = 'auto';
      contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
      setContentHeight(`${contentRef.current.scrollHeight}px`);
    }
  }, [content]);

  // Load draft from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem('ideaDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setContent(parsedDraft.content || '');
        setTags(parsedDraft.tags || []);
        // Media can't be stored in localStorage, only restore text and tags
      } catch (error) {
        console.error('Failed to parse saved draft:', error);
      }
    }
  }, []);

  // Auto-save draft
  useEffect(() => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    draftSaveTimerRef.current = setTimeout(() => {
      const draft = {
        content,
        tags,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem('ideaDraft', JSON.stringify(draft));
      setIsSaved(true);
      
      // Reset the "saved" indicator after 2 seconds
      setTimeout(() => setIsSaved(false), 2000);
    }, 1000);

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [content, tags]);

  // Search tags with debounce
  useEffect(() => {
    if (tagSearchTimerRef.current) {
      clearTimeout(tagSearchTimerRef.current);
    }

    if (tagInput.trim() && !isComposing) {
      setIsLoadingTags(true);
      tagSearchTimerRef.current = setTimeout(async () => {
        try {
          const results = await tagService.searchTags(tagInput.trim());
          const filteredTags = results
            .filter(tag => tag && tag.attributes) // 确保 tag 和 attributes 存在
            .map(tag => tag.attributes.name)
            .filter(tag => tag && !tags.includes(tag))
            .slice(0, 5);
          setSuggestedTags(filteredTags);
          setShowSuggestions(filteredTags.length > 0);
        } catch (error) {
          console.error('Failed to search tags:', error);
          setSuggestedTags([]);
          setShowSuggestions(false);
        } finally {
          setIsLoadingTags(false);
        }
      }, 300);
    } else {
      setSuggestedTags([]);
      setShowSuggestions(false);
      setIsLoadingTags(false);
    }

    return () => {
      if (tagSearchTimerRef.current) {
        clearTimeout(tagSearchTimerRef.current);
      }
    };
  }, [tagInput, tags, isComposing]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagInputRef.current && !tagInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (ideaData) => {
    try {
      // 创建片语
      await ideaService.createIdea(ideaData);
      
      // 如果有自定义的onSubmit回调，则调用它
      if (onSubmit) {
        onSubmit();
      }
      
      // 清除本地存储的草稿
      localStorage.removeItem('ideaDraft');
    } catch (error) {
      console.error('Failed to create idea:', error);
      throw error; // 将错误抛出，让IdeaForm组件处理
    }
  };

  return (
    <div className="modal-overlay">
      <div className="create-idea-modal">
        <IdeaForm 
          onClose={onClose} 
          onSubmit={handleSubmit} 
          isEditing={false}
        />
      </div>
    </div>
  );
};  

export default CreateIdeaModal;